'use client';

import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import { TrendingUp, AlertTriangle, BarChart2, DollarSign, Clock, Percent } from 'lucide-react';

const InvestmentScoreChart = dynamic(() => import('./InvestmentScoreChart'), { ssr: false });

interface InvestmentAnalysisProps {
  property: {
    id: string;
    investmentScore: number;
    rentalYield: string;
    amortization: string;
  };
}

const SCORE_FACTORS = [
  { id: 'sf-konum', label: 'Konum Talebi', score: 88, weight: 'Yüksek', color: 'bg-success' },
  { id: 'sf-getiri', label: 'Kira Getirisi', score: 72, weight: 'Yüksek', color: 'bg-primary' },
  { id: 'sf-fiyat', label: 'Fiyat/m² Uygunluğu', score: 76, weight: 'Orta', color: 'bg-primary' },
  { id: 'sf-deger', label: 'Tarihsel Değer Artışı', score: 81, weight: 'Orta', color: 'bg-success' },
  { id: 'sf-likidite', label: 'Piyasa Likiditesi', score: 85, weight: 'Orta', color: 'bg-success' },
  { id: 'sf-risk', label: 'Risk Faktörü', score: 79, weight: 'Düşük', color: 'bg-primary' },
];

export default function InvestmentAnalysis({ property }: InvestmentAnalysisProps) {
  const [rentEstimate] = useState({ min: 45000, max: 52000 });
  const purchasePrice = 12750000;
  const annualRentMin = rentEstimate.min * 12;
  const annualRentMax = rentEstimate.max * 12;
  const grossYieldMin = ((annualRentMin / purchasePrice) * 100).toFixed(1);
  const grossYieldMax = ((annualRentMax / purchasePrice) * 100).toFixed(1);
  const aidat = 3200 * 12;
  const maintenance = purchasePrice * 0.005;
  const insurance = 4800;
  const netAnnualMin = annualRentMin - aidat - maintenance - insurance;
  const netYield = ((netAnnualMin / purchasePrice) * 100).toFixed(1);

  return (
    <div className="space-y-8">
      {/* Score Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="card-elevated rounded-2xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-primary-light flex items-center justify-center">
              <BarChart2 size={20} className="text-primary" />
            </div>
            <div>
              <p className="text-sm font-bold text-foreground">Yatırım Skoru</p>
              <p className="text-xs text-muted-foreground">Evora AI Analizi</p>
            </div>
          </div>
          <InvestmentScoreChart score={property.investmentScore} />
          <div className="mt-4 space-y-2.5">
            {SCORE_FACTORS.map((factor) => (
              <div key={factor.id}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-foreground">{factor.label}</span>
                  <div className="flex items-center gap-2">
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${factor.weight === 'Yüksek' ? 'bg-danger-light text-danger' : factor.weight === 'Orta' ? 'bg-warning-light text-warning' : 'bg-muted text-muted-foreground'}`}>
                      {factor.weight}
                    </span>
                    <span className="text-xs font-bold text-foreground tabular-nums w-8 text-right">{factor.score}</span>
                  </div>
                </div>
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${factor.color}`}
                    style={{ width: `${factor.score}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Key Metrics */}
        <div className="space-y-3">
          {[
            {
              id: 'im-brut',
              icon: <Percent size={18} className="text-success" />,
              bg: 'bg-success-light',
              label: 'Brüt Kira Getirisi',
              value: `%${grossYieldMin} – %${grossYieldMax}`,
              sub: 'Yıllık tahmini',
              highlight: true,
            },
            {
              id: 'im-net',
              icon: <TrendingUp size={18} className="text-primary" />,
              bg: 'bg-primary-light',
              label: 'Net Kira Getirisi',
              value: `%${netYield}`,
              sub: 'Aidat, bakım ve sigorta sonrası',
              highlight: false,
            },
            {
              id: 'im-amortisman',
              icon: <Clock size={18} className="text-warning" />,
              bg: 'bg-warning-light',
              label: 'Amortisman Süresi',
              value: `${property.amortization} Yıl`,
              sub: 'Tahmini geri ödeme süresi',
              highlight: false,
            },
            {
              id: 'im-kira',
              icon: <DollarSign size={18} className="text-accent" />,
              bg: 'bg-orange-50',
              label: 'Tahmini Aylık Kira',
              value: `₺${rentEstimate.min.toLocaleString('tr-TR')} – ₺${rentEstimate.max.toLocaleString('tr-TR')}`,
              sub: 'Bölge ortalamasına göre',
              highlight: false,
            },
          ].map((metric) => (
            <div key={metric.id} className={`card-base p-4 flex items-center gap-4 ${metric.highlight ? 'border-success/30 bg-success-light/30' : ''}`}>
              <div className={`w-10 h-10 rounded-xl ${metric.bg} flex items-center justify-center flex-shrink-0`}>
                {metric.icon}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground font-medium">{metric.label}</p>
                <p className="text-lg font-extrabold text-foreground tabular-nums">{metric.value}</p>
                <p className="text-xs text-muted-foreground">{metric.sub}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Detailed Breakdown */}
      <div>
        <h3 className="text-lg font-bold text-foreground mb-4">Yıllık Gelir/Gider Analizi</h3>
        <div className="card-elevated rounded-2xl overflow-hidden">
          <div className="grid grid-cols-2 px-5 py-3 bg-muted border-b border-border text-xs font-bold text-muted-foreground uppercase tracking-wider">
            <span>Kalem</span>
            <span className="text-right">Yıllık Tutar</span>
          </div>
          {[
            { id: 'ba-kira-min', label: 'Tahmini Kira Geliri (Min)', value: `+₺${annualRentMin.toLocaleString('tr-TR')}`, positive: true },
            { id: 'ba-kira-max', label: 'Tahmini Kira Geliri (Max)', value: `+₺${annualRentMax.toLocaleString('tr-TR')}`, positive: true },
            { id: 'ba-aidat', label: 'Aidat', value: `-₺${aidat.toLocaleString('tr-TR')}`, positive: false },
            { id: 'ba-bakim', label: 'Bakım & Onarım (%0.5)', value: `-₺${Math.round(maintenance).toLocaleString('tr-TR')}`, positive: false },
            { id: 'ba-sigorta', label: 'Konut Sigortası', value: `-₺${insurance.toLocaleString('tr-TR')}`, positive: false },
            { id: 'ba-bos', label: 'Boşluk Payı (%5)', value: `-₺${Math.round(annualRentMin * 0.05).toLocaleString('tr-TR')}`, positive: false },
          ].map((row) => (
            <div key={row.id} className="grid grid-cols-2 px-5 py-3.5 border-b border-border last:border-0 hover:bg-muted/50 transition-colors">
              <span className="text-sm font-medium text-foreground">{row.label}</span>
              <span className={`text-sm font-bold text-right tabular-nums ${row.positive ? 'text-success' : 'text-danger'}`}>{row.value}</span>
            </div>
          ))}
          <div className="grid grid-cols-2 px-5 py-4 bg-primary-light border-t-2 border-primary/20">
            <span className="text-sm font-extrabold text-primary">Tahmini Net Yıllık Gelir</span>
            <span className="text-sm font-extrabold text-primary text-right tabular-nums">₺{Math.round(netAnnualMin).toLocaleString('tr-TR')}</span>
          </div>
        </div>
      </div>

      {/* Comparable Properties */}
      <div>
        <h3 className="text-lg font-bold text-foreground mb-4">Bölge Karşılaştırması</h3>
        <div className="card-base p-5">
          <div className="grid grid-cols-3 gap-4 text-center">
            {[
              { id: 'comp-this', label: 'Bu İlan', price: '₺85.000', yield: '%5.8', days: '3', highlight: true },
              { id: 'comp-avg', label: 'Kadıköy Ort.', price: '₺87.450', yield: '%5.6', days: '28', highlight: false },
              { id: 'comp-tr', label: 'İstanbul Ort.', price: '₺87.450', yield: '%5.4', days: '35', highlight: false },
            ].map((comp) => (
              <div key={comp.id} className={`p-3 rounded-xl ${comp.highlight ? 'bg-primary-light border-2 border-primary' : 'bg-muted'}`}>
                <p className={`text-xs font-bold mb-2 ${comp.highlight ? 'text-primary' : 'text-muted-foreground'}`}>{comp.label}</p>
                <p className="text-sm font-extrabold text-foreground tabular-nums">{comp.price}/m²</p>
                <p className="text-xs text-success font-bold mt-1">{comp.yield} getiri</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{comp.days} gün ort.</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-3 text-center">Benzer 14 ilan ile karşılaştırılmıştır. Veriler tahminidir.</p>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="p-4 rounded-xl bg-warning-light border border-warning/20 flex items-start gap-3">
        <AlertTriangle size={16} className="text-warning flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-bold text-warning mb-1">Önemli Uyarı</p>
          <p className="text-xs text-warning/80 leading-relaxed">
            Bu analiz yalnızca bilgilendirme amaçlıdır ve yatırım tavsiyesi niteliği taşımaz. Tüm kira tahminleri ve getiri hesaplamaları tahmini olup gerçek sonuçlar farklılık gösterebilir. Yatırım kararı vermeden önce profesyonel mali danışman ile görüşmenizi öneririz.
          </p>
        </div>
      </div>
    </div>
  );
}